import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const MASKED_SECRET = '*******************************';
const KEEP_SECRET_SENTINEL = '***KEEP_EXISTING***';
const MESSAGING_ENDPOINT_BASE = 'https://app.chattitude.ai/slickbots-ws/api/chat/messages';

const MsTeamsCredentialsModal = ({ open, onClose, onCredentialsSaved, tenantId, agentId }) => {
  const [credentials, setCredentials] = useState({
    msTenantId: '',
    msClientId: '',
    msClientSecret: ''
  });
  const [hasExistingSecret, setHasExistingSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [encryptedAgentId, setEncryptedAgentId] = useState(null);
  const [encryptedTenantId, setEncryptedTenantId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const fetchCredentials = async () => {
      if (!tenantId || !agentId) {
        setCredentials({ msTenantId: '', msClientId: '', msClientSecret: '' });
        setHasExistingSecret(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const jwtToken = Cookies.getJwtToken();
        const tenant = Cookies.getTenantId();
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

        const res = await axios.get(`${API_URL}/agent-ms-teams-credentials`, {
          params: { tenantId, agentId },
          headers
        });
        if (res.data) {
          setCredentials({
            msTenantId: res.data.msTenantId || '',
            msClientId: res.data.msClientId || '',
            msClientSecret: MASKED_SECRET
          });
          setHasExistingSecret(true);
        } else {
          setCredentials({ msTenantId: '', msClientId: '', msClientSecret: '' });
          setHasExistingSecret(false);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setCredentials({ msTenantId: '', msClientId: '', msClientSecret: '' });
          setHasExistingSecret(false);
        } else {
          setCredentials({ msTenantId: '', msClientId: '', msClientSecret: '' });
          setHasExistingSecret(false);
          setError('Failed to fetch credentials.');
        }
      } finally {
        setLoading(false);
      }
    };
    if (open && tenantId && agentId) {
      fetchCredentials();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [open, tenantId, agentId]);

  useEffect(() => {
    const fetchEncryptedIds = async () => {
      if (!open || !agentId) return;
      try {
        const response = await axios.post('/agents/encrypt-ids', null, {
          params: { agentId }
        });
        setEncryptedAgentId(response.data.encryptedAgentId);
        setEncryptedTenantId(response.data.encryptedTenantId);
      } catch (err) {
        console.error('Error fetching encrypted IDs:', err);
        setEncryptedAgentId('');
        setEncryptedTenantId('');
      }
    };
    fetchEncryptedIds();
  }, [open, agentId]);

  const getMessagingEndpointUrl = () => {
    if (!encryptedAgentId || !encryptedTenantId || !credentials.msClientId?.trim()) return '';
    // Use raw encrypted values (keep == padding) for display/copy consistency with DeploymentDetail
    const appId = credentials.msClientId.trim();
    const q = [
      `agentId=${encodeURIComponent(encryptedAgentId)}`,
      `tenantId=${encodeURIComponent(encryptedTenantId)}`,
      `appId=${encodeURIComponent(appId)}`
    ].join('&');
    return `${MESSAGING_ENDPOINT_BASE}?${q}`;
  };

  const handleCopyMessagingEndpoint = () => {
    const url = getMessagingEndpointUrl();
    if (!url) return;
    navigator.clipboard.writeText(url)
      .then(() => setCopySuccess(true))
      .catch(err => console.error('Failed to copy:', err));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'msClientSecret' && hasExistingSecret && value !== MASKED_SECRET) {
      setHasExistingSecret(false);
    }
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!credentials.msTenantId?.trim()) return 'MS Tenant ID is required.';
    if (!credentials.msClientId?.trim()) return 'MS Client ID is required.';
    if (!hasExistingSecret && !credentials.msClientSecret?.trim()) return 'MS Client Secret is required for new credentials.';
    if (hasExistingSecret && credentials.msClientSecret !== MASKED_SECRET && !credentials.msClientSecret?.trim()) return 'MS Client Secret is required.';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const jwtToken = Cookies.getJwtToken();
      const tenant = Cookies.getTenantId();
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

      const payload = {
        tenantId: parseInt(tenantId, 10),
        agentId: parseInt(agentId, 10),
        msTenantId: credentials.msTenantId.trim(),
        msClientId: credentials.msClientId.trim(),
        msClientSecret: (hasExistingSecret && credentials.msClientSecret === MASKED_SECRET)
          ? KEEP_SECRET_SENTINEL
          : credentials.msClientSecret?.trim() || ''
      };

      await axios.post(`${API_URL}/agent-ms-teams-credentials`, payload, { headers });
      setSnackbar({ open: true, message: 'Credentials saved successfully!', severity: 'success' });
      onCredentialsSaved && onCredentialsSaved();
      setTimeout(() => {
        onClose && onClose();
      }, 2000);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save credentials.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseIcon = () => {
    onClose && onClose();
  };

  const secretPlaceholder = hasExistingSecret ? 'Leave as-is to keep existing secret' : '';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 99999 }}
        PaperProps={{ style: { minWidth: 480, zIndex: 99999 } }}
      >
        <DialogTitle sx={{ position: 'relative', pr: 6 }}>
          MS Teams Credentials
          <IconButton
            onClick={handleCloseIcon}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="MS Tenant ID (Azure AD Directory ID)"
                name="msTenantId"
                value={credentials.msTenantId}
                onChange={handleChange}
                required
                fullWidth
                placeholder="e.g. f1821055-fa32-43c0-bdca-9080f91ff7b9"
              />
              <TextField
                label="MS Client ID (Bot App ID)"
                name="msClientId"
                value={credentials.msClientId}
                onChange={handleChange}
                required
                fullWidth
                placeholder="e.g. 137efd12-8ddd-420a-90d5-fa63a5d3cb20"
              />
              <TextField
                label="MS Client Secret"
                name="msClientSecret"
                type="password"
                value={credentials.msClientSecret}
                onChange={handleChange}
                required={!hasExistingSecret}
                fullWidth
                placeholder={secretPlaceholder}
                helperText={hasExistingSecret ? 'Enter a new value to overwrite, or leave as-is to keep existing.' : 'Required for Bot Framework OAuth.'}
              />
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Messaging Endpoint</Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    onClick={handleCopyMessagingEndpoint}
                    disabled={!getMessagingEndpointUrl()}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                label="Bot messaging endpoint URL (configure in Bot Framework)"
                value={getMessagingEndpointUrl()}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  '& .MuiInputBase-input': { fontFamily: 'monospace' }
                }}
              />
              <Box sx={{ mt: 1, p: 2, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  These credentials are used to obtain OAuth tokens for the Bot Framework when your agent receives messages via MS Teams or Web Chat.
                  Configure your Bot in Azure Portal and copy the Tenant ID, Application (client) ID, and Client Secret here.
                </Typography>
              </Box>
              {error && (
                <Typography color="error" variant="body2">{error}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 2, px: 3 }}>
          <Button onClick={handleSave} variant="outlined" color="primary" disabled={loading} sx={{ minWidth: 180, py: 1.2 }}>
            SAVE &amp; Close
          </Button>
        </DialogActions>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Snackbar
          open={copySuccess}
          autoHideDuration={2000}
          onClose={() => setCopySuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setCopySuccess(false)} severity="success" variant="filled">
            Messaging endpoint copied to clipboard!
          </Alert>
        </Snackbar>
      </Dialog>
    </>
  );
};

export default MsTeamsCredentialsModal;
